// FILE: components/seo/DetailBreadcrumb.tsx
"use client";
import HardNavLink from "@/components/shared/HardNavLink";
import { ChevronRight, Home, ArrowLeft } from "lucide-react";

export interface DetailBreadcrumbItem {
  label: string;
  href?: string;
}

interface Props {
  items: DetailBreadcrumbItem[];
  className?: string;
}

export default function DetailBreadcrumb({ items, className = "" }: Props) {
  const fullItems: DetailBreadcrumbItem[] = [
    { label: "Trang chủ", href: "/" },
    ...items,
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: fullItems.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.label,
      ...(item.href && {
        item: (process.env.NEXT_PUBLIC_SITE_URL ?? "https://web.jb.com.vn") + item.href,
      }),
    })),
  };

  function smartBack() {
    if (typeof window === "undefined") return;
    if (document.referrer) {
      try {
        const ref = new URL(document.referrer);
        if (ref.origin === window.location.origin && window.history.length > 1) {
          window.history.back();
          return;
        }
      } catch {}
    }
    // Fallback: về parent (item đầu tiên có href, bỏ Home)
    const parent = items.find((i) => i.href);
    if (parent?.href) window.location.href = parent.href;
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={smartBack}
          aria-label="Quay lại"
          className="shrink-0 w-7 h-7 rounded-lg border border-border bg-white flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>

        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1 text-[12px] text-muted-foreground overflow-x-auto scrollbar-hide whitespace-nowrap min-w-0 flex-1"
        >
          {fullItems.map((item, i) => {
            const isLast = i === fullItems.length - 1;
            const isFirst = i === 0;
            return (
              <div key={i} className="flex items-center gap-1 shrink-0">
                {!isFirst && <ChevronRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />}
                {item.href && !isLast ? (
                  <HardNavLink
                    href={item.href}
                    className="hover:text-teal-700 transition-colors flex items-center gap-1 shrink-0"
                  >
                    {isFirst && <Home className="w-3 h-3" />}
                    {item.label}
                  </HardNavLink>
                ) : (
                  <span className={`font-semibold flex items-center gap-1 ${isLast ? "text-foreground max-w-[300px] truncate" : ""}`}>
                    {isFirst && <Home className="w-3 h-3" />}
                    {item.label}
                  </span>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
}
